import { useState } from 'react';
import { Input, Label, Message, Slider } from '@iwoca/orion';

import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';

import { TSupportedLocales } from 'types';
import InputFormatter from 'utils/inputFormatters/inputFormatters';
import {
  MAX_DE_AMOUNT,
  MAX_UK_AMOUNT,
  MIN_DE_AMOUNT,
  MIN_UK_AMOUNT,
} from '../filterConfigs.helper';
import standAloneCalculatorStyles from '../StandAloneCalculator.module.css';
import styles from './LoanAmountFilter.module.css';

type TProps = {
  value: string;
  onChange: (amount: string) => void;
};

export const getConfig = (locale: TSupportedLocales) => {
  if (locale === 'de') {
    return {
      min: Number(MIN_DE_AMOUNT),
      max: Number(MAX_DE_AMOUNT),
    };
  }

  return {
    min: Number(MIN_UK_AMOUNT),
    max: Number(MAX_UK_AMOUNT),
  };
};

export const LoanAmountFilter = ({ value, onChange }: TProps) => {
  const [error, setError] = useState<string | null>();
  const { formatCurrency } = useNumberFormat();
  const { locale } = useLocale();

  const loanConfig = getConfig(locale);

  const handleBlur = (newValue: string) => {
    setError(null);
    if (Number(newValue) > loanConfig.max) {
      onChange(loanConfig.max.toString());
    } else if (Number(newValue) < loanConfig.min) {
      onChange(loanConfig.min.toString());
    }
  };

  const handleNumberChange = (value: string) => {
    setError(null);

    if (Number(value) > loanConfig.max) {
      setError(t`Please enter a maximum of ${loanConfig.max}`);
    } else if (Number(value) < loanConfig.min) {
      setError(t`Please enter a minimum of ${loanConfig.min}`);
    }

    onChange(InputFormatter.Integer(value));
  };

  return (
    <div className={styles.container}>
      <div className={styles.inputContainer}>
        <Label htmlFor="loan-amount" className={styles.label}>
          <Trans>Loan amount</Trans>
        </Label>
        <Input
          id="loan-amount"
          className={styles.input}
          value={value}
          onChange={(e) => handleNumberChange(e.target.value)}
          onBlur={(e) => handleBlur(e.target.value)}
        />
      </div>
      <Slider
        labelClassName={standAloneCalculatorStyles['label']}
        data-testid="loan-amount-slider"
        value={value}
        step={1000}
        leftLabel={formatCurrency(loanConfig.min)}
        rightLabel={formatCurrency(loanConfig.max)}
        min={loanConfig.min}
        max={loanConfig.max}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && (
        <Message
          className={styles.error}
          size="small"
          type="error"
          message={error}
        />
      )}
    </div>
  );
};
